import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramFumehoodPressureDifferential } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.fumehoodPressureDifferential.v1';

type Fields = { roomPressure: string; hoodPressure: string; differential: string };
const EMPTY: Fields = { roomPressure: '', hoodPressure: '', differential: '' };

/**
 * Fumehood Pressure Differential — ΔP = Room − Hood.
 */
const FumehoodPressureDifferentialScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) { setError('Fill any 2 of the 3 fields to solve for the missing one.'); return; }
    if (filled === 3) { setError('All 3 values are filled — clear one to solve for it.'); return; }

    const room = toImp(fields.roomPressure, 'pressure');
    const hood = toImp(fields.hoodPressure, 'pressure');
    const dp = toImp(fields.differential, 'pressure');

    if (fields.differential === '') {
      setSolvedFor('differential');
      set({ differential: fromImp(room - hood, 'pressure', 3) });
    } else if (fields.roomPressure === '') {
      setSolvedFor('roomPressure');
      set({ roomPressure: fromImp(hood + dp, 'pressure', 3) });
    } else if (fields.hoodPressure === '') {
      setSolvedFor('hoodPressure');
      set({ hoodPressure: fromImp(room - dp, 'pressure', 3) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.roomPressure && { label: 'Room Pressure', value: fields.roomPressure, unit: unit('pressure'), emphasis: solvedFor === 'roomPressure' },
    fields.hoodPressure && { label: 'Hood Pressure', value: fields.hoodPressure, unit: unit('pressure'), emphasis: solvedFor === 'hoodPressure' },
    fields.differential && { label: 'ΔP', value: fields.differential, unit: unit('pressure'), emphasis: solvedFor === 'differential' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Pressure Differential"
      subtitle="Calculate pressure differential using ΔP = Room Pressure − Hood Pressure"
      formula={
        <>
          <FormulaLine>ΔP = Room Pressure − Hood Pressure</FormulaLine>
          <FormulaNote>Pressures in {unit('pressure')}.</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: ΔP = P_room − P_hood"
      diagram={<DiagramFumehoodPressureDifferential />}
      outputSlots={{
        pressure_differential_iwg: fields.differential ? toImp(fields.differential, 'pressure') : undefined,
        pressure_differential_pa: fields.differential ? toImp(fields.differential, 'pressure') * 249.0889 : undefined,
      }}
      onPrefill={(values) => {
        if (fields.differential) return;
        if (values.pressure_differential_iwg != null && !fields.roomPressure && !fields.hoodPressure) {
          set({ differential: fromImp(values.pressure_differential_iwg, 'pressure', 3) });
        } else if (values.pressure_differential_pa != null && !fields.roomPressure && !fields.hoodPressure) {
          set({ differential: fromImp(values.pressure_differential_pa / 249.0889, 'pressure', 3) });
        }
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Room Pressure (${unit('pressure')})`}
          value={fields.roomPressure}
          onChangeText={(v) => set({ roomPressure: v })}
          rightText={unit('pressure')}
          maxDecimals={3}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Hood Pressure (${unit('pressure')})`}
          value={fields.hoodPressure}
          onChangeText={(v) => set({ hoodPressure: v })}
          rightText={unit('pressure')}
          maxDecimals={3}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`ΔP (${unit('pressure')})`}
          value={fields.differential}
          onChangeText={(v) => set({ differential: v })}
          rightText={unit('pressure')}
          allowNegative
          maxDecimals={3}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default FumehoodPressureDifferentialScreen;
